import { InputManager } from './input';
import { ParticleSystem } from './particle';

export interface LittleJSEngineConfig {
  width: number;
  height: number;
  pixelScale: number;
  background: string;
}

export interface EngineContext {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  input: InputManager;
  particles: ParticleSystem;
  config: LittleJSEngineConfig;
  time: number;
  delta: number;
}

export interface EngineScene {
  init?(context: EngineContext): void;
  update(context: EngineContext): void;
  render(context: EngineContext): void;
}

export class LittleJSEngine {
  private ctx: CanvasRenderingContext2D;
  private input: InputManager;
  private particles = new ParticleSystem();
  private config: LittleJSEngineConfig;
  private time = 0;
  private lastFrame = 0;
  private running = false;
  private scene?: EngineScene;
  private canvasPixelated = false;
  private tilesPixelated = true;
  private sharpnessBoost = 1;

  constructor(
    private canvas: HTMLCanvasElement,
    config?: Partial<LittleJSEngineConfig>,
  ) {
    const defaultConfig: LittleJSEngineConfig = {
      width: 320,
      height: 180,
      pixelScale: 3,
      background: '#0f1d0f',
    };
    this.config = { ...defaultConfig, ...config };

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Unable to acquire 2D context');
    }
    this.ctx = ctx;
    this.input = new InputManager(window);
    this.resize();
    window.addEventListener('resize', () => this.resize());
  }

  setScene(scene: EngineScene): void {
    this.scene = scene;
    this.scene.init?.(this.createContext(0));
  }

  start(): void {
    if (this.running) {
      return;
    }
    this.running = true;
    this.lastFrame = performance.now();
    requestAnimationFrame(this.loop);
  }

  stop(): void {
    this.running = false;
  }

  private resize(): void {
    const { width, height, pixelScale } = this.config;
    this.canvas.width = width;
    this.canvas.height = height;
    this.canvas.style.width = `${width * pixelScale}px`;
    this.canvas.style.height = `${height * pixelScale}px`;
    this.applyRenderSettings();
  }

  setCanvasPixelated(enabled: boolean): void {
    this.canvasPixelated = enabled;
    this.applyRenderSettings();
  }

  setTilesPixelated(enabled: boolean): void {
    this.tilesPixelated = enabled;
    this.applyRenderSettings();
  }

  setSharpness(amount: number): void {
    this.sharpnessBoost = Math.max(1, amount);
    this.applyRenderSettings();
  }

  private loop = (timestamp: number): void => {
    if (!this.running) {
      return;
    }

    const delta = Math.min((timestamp - this.lastFrame) / 1000, 0.25);
    this.lastFrame = timestamp;
    this.time += delta;
    this.input.update();
    const context = this.createContext(delta);
    this.update(context);
    this.render(context);
    requestAnimationFrame(this.loop);
  };

  private update(context: EngineContext): void {
    this.particles.update(context.delta);
    this.scene?.update(context);
  }

  private render(context: EngineContext): void {
    const { ctx } = context;
    ctx.fillStyle = this.config.background;
    ctx.fillRect(0, 0, this.config.width, this.config.height);
    this.scene?.render(context);
    this.particles.render(ctx);
  }

  private createContext(delta: number): EngineContext {
    return {
      canvas: this.canvas,
      ctx: this.ctx,
      input: this.input,
      particles: this.particles,
      config: this.config,
      time: this.time,
      delta,
    };
  }

  private applyRenderSettings(): void {
    this.ctx.imageSmoothingEnabled = !this.tilesPixelated;
    this.canvas.style.imageRendering = this.canvasPixelated ? 'pixelated' : 'auto';
    if (this.sharpnessBoost > 1) {
      const saturation = Math.min(this.sharpnessBoost * 1.05, 1.35);
      this.canvas.style.filter = `contrast(${this.sharpnessBoost}) saturate(${saturation})`;
    } else {
      this.canvas.style.filter = 'none';
    }
  }
}
